import { useEffect, useState } from 'react'
import { Card } from '../Card'
import { CardHeader } from '../CardHeader'
import PlusIcon from '@/CustomIcons/PlusIcon'
import { Button } from '@/UIComponents2/Button'
import Tooltip from '@/UIComponents2/Tooltip'
import { useRoomContext } from '@/RoomContext'
import { SubRoomFragment, useUserContext } from '@/UserContext'
import useAnalytics from '@/services/useAnalytics'
import useRoomActions from '@/services/useRoomActions'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { cn } from '@/utlis/cn'
import GearIcon from '@/CustomIcons/GearIcon'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import LoginIcon from '@/CustomIcons/LoginIcon'
import { wsConnection } from '@/services/WSConnectionInstance'
import { WSConnectionSendError } from '@/services/WSConnectionManager'
import { WSRoomPublicInfo, WSRoomPublicInfoClient } from '@/pages/CollaboratePage/Types'
import UserIcon from '@/CustomIcons/UserIcon'
import { Trans, useTranslation } from 'react-i18next'
import StudioIcon from '@/CustomIcons/StudioIcon'
import EditStudioSettingsDialog from './internal/EditStudioSettingsDialog'
import { Checkbox } from '@/UIComponents2/Checkbox'
import Api from '@/services/Api'

function StudioManagementSection({ parentRoomId }: { parentRoomId: string }) {
    const [roomState, roomDispatch] = useRoomContext()

    const [isLoading, setIsLoading] = useState(false)
    const [isUserSubroomAssignmentActive, setIsUserSubroomAssignmentActive] = useState(
        roomState.parentRoom
            ? roomState.parentRoom.isUserSubroomAssignmentActive
            : roomState.isUserSubroomAssignmentActive
    )
    const [isUserSubroomAutoAssigned, setIsUserSubroomAutoAssigned] = useState(
        roomState.parentRoom ? roomState.parentRoom.isUserSubroomAutoAssigned : roomState.isUserSubroomAutoAssigned
    )

    const onBooleanPropertyChanged = async (
        propertyName: 'isUserSubroomAssignmentActive' | 'isUserSubroomAutoAssigned',
        value: boolean
    ) => {
        try {
            setIsLoading(true)
            if (propertyName === 'isUserSubroomAssignmentActive') {
                setIsUserSubroomAssignmentActive(value)
            } else if (propertyName === 'isUserSubroomAutoAssigned') {
                setIsUserSubroomAutoAssigned(value)
            }

            const result = await Api.updateRoom(parentRoomId, {
                [propertyName]: value,
            })

            if (result && result.room) {
                if (propertyName === 'isUserSubroomAssignmentActive') {
                    setIsUserSubroomAssignmentActive(result.room.isUserSubroomAssignmentActive ?? false)
                } else if (propertyName === 'isUserSubroomAutoAssigned') {
                    setIsUserSubroomAutoAssigned(result.room.isUserSubroomAutoAssigned ?? false)
                }

                if (roomState.parentRoom) {
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            parentRoom: {
                                ...roomState.parentRoom,
                                isUserSubroomAssignmentActive: result.room.isUserSubroomAssignmentActive ?? false,
                                isUserSubroomAutoAssigned: result.room.isUserSubroomAutoAssigned ?? false,
                            },
                        },
                    })
                } else {
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            isUserSubroomAssignmentActive: result.room.isUserSubroomAssignmentActive ?? false,
                            isUserSubroomAutoAssigned: result.room.isUserSubroomAutoAssigned ?? false,
                        },
                    })
                }
            }
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }
    const { t } = useTranslation()

    return (
        <Card className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
                <Button
                    variant="primary"
                    disabled={isLoading}
                    onClick={() =>
                        onBooleanPropertyChanged('isUserSubroomAssignmentActive', !isUserSubroomAssignmentActive)
                    }
                >
                    {!isUserSubroomAssignmentActive ? t('Move Users to Studios') : t('Move Users to Main Room')}
                </Button>
                <Checkbox
                    label="Auto assign users to studios"
                    checked={isUserSubroomAutoAssigned}
                    disabled={isLoading}
                    onChange={(b) => onBooleanPropertyChanged('isUserSubroomAutoAssigned', b)}
                />
            </div>
        </Card>
    )
}

export function StudioSettings() {
    const [roomState, roomDispatch] = useRoomContext()
    const { switchRoom, createSubroom } = useRoomActions()
    const { logUserActivityWithContext } = useAnalytics()

    const [changingRoom, setChangingRoom] = useState(false)
    const [isCreatingSubroom, setIsCreatingSubroom] = useState(false)
    const [isEditStudioDialogOpen, setIsEditStudioDialogOpen] = useState<{
        subroom: SubRoomFragment | null // null subroom means create, otherwise edit the subroom
    } | null>(null)

    const rooms = roomState.subrooms ?? []
    const currentRoomId = roomState.roomUrn || roomState.preRoomUrn
    const parentRoomId = roomState.parentRoom?._id ?? currentRoomId

    const roomClientInfo = useSubroomInfo(parentRoomId, currentRoomId)

    const isHost = roomState.isHost

    async function handleAddStudio(data: { subroomName: string; collaborators: string[] }) {
        setIsCreatingSubroom(true)
        await createSubroom(data)
        setIsCreatingSubroom(false)
        logUserActivityWithContext(AnalyticsEvents.CLICKED_CREATE_BREAKOUT_SESSION)
        setIsEditStudioDialogOpen(null)
    }

    async function handleUpdateStudio(data: { roomUrn: string | null; subroomName: string; collaborators: string[] }) {
        setIsCreatingSubroom(true)
        if (!data.roomUrn) {
            // contract: if updating a subroom, then should have `data.roomUrn`.
            setIsEditStudioDialogOpen(null)
            setIsCreatingSubroom(false)
            return
        }

        await Api.updateRoom(data.roomUrn, {
            name: data.subroomName,
            // TODO: not implemented
            // collaborators: data.collaborators,
        })

        const password = roomState.parentRoom?.password || undefined
        const parentRoom = await Api.getRoom(parentRoomId, password)
        if (parentRoom) {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    subrooms: parentRoom.subrooms || [],
                },
            })
        }

        setIsEditStudioDialogOpen(null)
        setIsCreatingSubroom(false)
    }

    const handleJoin = async (roomId: string) => {
        if (currentRoomId === roomId || changingRoom) {
            return
        }

        if (roomId === parentRoomId) {
            logUserActivityWithContext(AnalyticsEvents.CLICKED_JOIN_MAIN_ROOM_SESSION)
        } else {
            logUserActivityWithContext(AnalyticsEvents.CLICKED_JOIN_BREAKOUT_SESSION, { roomId })
        }

        setChangingRoom(true)
        setTimeout(() => setChangingRoom(false), 1500)
        switchRoom(roomId)
    }

    return (
        <div className="flex flex-col gap-[10px]">
            <Card className="flex flex-col gap-4 px-0 md:px-0">
                <div className="flex items-center justify-between px-[30px]">
                    <CardHeader icon={StudioIcon} title="Studio Breakout Sessions" />
                    <Tooltip text="Add Studio">
                        <Button
                            onClick={() => setIsEditStudioDialogOpen({ subroom: null })}
                            className={cn(
                                'w-[34px] h-[34px] rounded-full text-black bg-muse-primary-gradient hover:bg-muse-primary-gradient-hover',
                                (isCreatingSubroom || !roomState.isHost) && 'opacity-50 cursor-not-allowed'
                            )}
                            disabled={isCreatingSubroom || !roomState.isHost}
                        >
                            <PlusIcon style={{ fontSize: '22px' }} />
                        </Button>
                    </Tooltip>
                </div>
                <div className="flex flex-col">
                    {parentRoomId && (
                        <RoomListItem
                            mainRoom
                            roomId={parentRoomId}
                            active={currentRoomId === parentRoomId}
                            onJoin={() => handleJoin(parentRoomId)}
                            clients={roomClientInfo[parentRoomId] ?? []}
                            changingRoom={changingRoom}
                            isHost={roomState.isHost}
                        />
                    )}
                    {rooms.map((subroom) => (
                        <RoomListItem
                            key={subroom._id}
                            subroom={subroom}
                            roomId={subroom._id}
                            active={currentRoomId === subroom._id}
                            onJoin={() => handleJoin(subroom._id)}
                            onEdit={() => {
                                setIsEditStudioDialogOpen({ subroom })
                            }}
                            clients={roomClientInfo[subroom._id] ?? []}
                            changingRoom={changingRoom}
                            isHost={roomState.isHost}
                        />
                    ))}
                </div>
            </Card>

            {isHost && <StudioManagementSection parentRoomId={parentRoomId} />}

            {isEditStudioDialogOpen && (
                <EditStudioSettingsDialog
                    subroom={isEditStudioDialogOpen.subroom}
                    onSubmit={(mode, data) => {
                        mode === 'create' ? handleAddStudio(data) : handleUpdateStudio(data)
                    }}
                    isLoading={isCreatingSubroom}
                    onClose={() => setIsEditStudioDialogOpen(null)}
                />
            )}
        </div>
    )
}

type RoomListItemProps = {
    roomId: string
    active?: boolean
    subroom?: SubRoomFragment
    mainRoom?: boolean
    onEdit?: () => void
    onJoin?: () => void
    clients?: WSRoomPublicInfoClient[]
    changingRoom?: boolean
    isHost: boolean
}
function RoomListItem(props: RoomListItemProps) {
    const { t } = useTranslation()
    const name = props.mainRoom ? 'Main room' : props.subroom?.name

    const disableJoin = props.changingRoom || props.active

    return (
        <div className="flex items-center justify-between h-[64px] border-t-1 border-white/3 px-[30px]">
            <div className="flex gap-3 items-center">
                <div
                    className={cn('w-[10px] h-[10px] rounded-full', props.active ? 'bg-muse-yellow' : 'bg-neutral-500')}
                ></div>
                <div className={props.active ? '' : 'font-light'}>{name}</div>
            </div>

            <div className="flex gap-5 items-center">
                <div className="flex gap-1 items-center opacity-65 t-sm-light">
                    <UserIcon style={{ fontSize: '1.1rem' }} />
                    <Trans i18nKey="{{count}} Participant" count={props.clients?.length ?? 0}>
                        {'{{count}}'} Participants
                    </Trans>
                </div>
                {props.isHost && (
                    <div>
                        <Tooltip text={t('Edit studio')}>
                            <Button className="opacity-75 hover:opacity-100" onClick={props.onEdit}>
                                <GearIcon style={{ fontSize: '1.3rem' }} />
                            </Button>
                        </Tooltip>
                    </div>
                )}
                <div>
                    <Tooltip text={t('Join studio')}>
                        <CircleIconButton
                            className={cn(
                                'shadow-glow w-[36px] h-[36px] min-w-[36px] min-h-[36px]',
                                disableJoin && 'opacity-50 cursor-not-allowed'
                            )}
                            onClick={props.onJoin}
                            disabled={disableJoin}
                        >
                            <LoginIcon style={{ fontSize: '1.3rem' }} />
                        </CircleIconButton>
                    </Tooltip>
                </div>
            </div>
        </div>
    )
}

function useSubroomInfo(parentRoomId: string, currentRoomId: string): Record<string, WSRoomPublicInfoClient[]> {
    const [userState] = useUserContext()
    const [roomClientInfo, setRoomClientInfo] = useState<Record<string, WSRoomPublicInfoClient[]>>({})

    useEffect(() => {
        const getRoomPublicInfo = async () => {
            const response: WSRoomPublicInfo | WSConnectionSendError = await wsConnection.sendMessage(
                {
                    type: 'get-room-public-info',
                    data: {
                        roomUrn: parentRoomId || currentRoomId,
                        sessionId: userState.sessionId,
                    },
                },
                {
                    isReliable: true,
                    checkAck: true,
                }
            )

            if (response instanceof WSConnectionSendError) {
                return null
            }

            return response
        }

        const updateRoomClientInfo = async () => {
            const data = await getRoomPublicInfo()
            if (data) {
                const roomClientInfo: Record<string, WSRoomPublicInfoClient[]> = {}
                data.subrooms.forEach((subroom) => {
                    roomClientInfo[subroom.roomUrn] = subroom.clients
                })
                roomClientInfo[parentRoomId] = data.clients
                setRoomClientInfo(roomClientInfo)
            }
        }

        setTimeout(() => updateRoomClientInfo(), 300)
        const t = setInterval(() => {
            updateRoomClientInfo()
        }, 1000 * 10)

        return () => {
            clearInterval(t)
        }
    }, [userState.sessionId, parentRoomId, currentRoomId])

    return roomClientInfo
}
